import { format } from 'date-fns';
import { IQueryFilterWrapper, IMetricFilterWrapper } from '../../interfaces/query-filter.interface'
import { Dataset } from '../../models/analytics-api/dataset.model';
import { Query } from '../../models/analytics-api/query.model';
import { sortBy, uniqBy } from 'lodash';
import {
  Schema,
  Dimension,
  DimensionProperty,
  Metric,
  Rollup
} from '../../models/analytics-api/schema.model';
import { DateRange } from '../date-range-picker/types';

export class QueryBuilder {
  query: Query
  schema: Schema
  dates: any
  isValid = true
  isDimensionsValid = false
  dimensionsError: string
  metricsError: string

  constructor(query: Query) {
    this.query = query
    this.schema = query.schema
    this.dates = query.getDates()
    this.isValid = query.isValid()
    this.dimensionsError = this.query.validateDimensions()
    this.metricsError = this.query.validateMetrics()
    this.isDimensionsValid = !this.dimensionsError
  }

  setDimensions(dimensions: Dimension[]): QueryBuilder {
    if (dimensions.length == 0) {
      this.query.groupBy = null
    }

    const newDimensions = uniqBy([...this.schema.requiredDimensions(), ...dimensions], 'name')
    if (this.query.groupBy == null) {
      this.query.groupBy = newDimensions
    } else {
      const newGroupBy = dimensions.filter(dimension => !this.query.dimensions.includes(dimension))
      this.query.groupBy = sortBy(
        uniqBy(
          [...this.query.groupBy, ...newGroupBy].filter(dimension => dimensions.includes(dimension)),
          'name'
        ),
        dimension => dimensions.indexOf(dimension)
      )
    }
    this.query.dimensions = newDimensions

    this.query.isDirty = true
    this.dimensionsError = this.query.validateDimensions()
    this.filterOrder()
    this.isValid = this.query.isValid()
    this.isDimensionsValid = !this.dimensionsError

    if (this.query.dimensionProperties) {
      const newDimensionProperties = {}
      for (const dimension of this.query.dimensions) {
        const dimensionProperty = this.query.dimensionProperties[dimension.name]
        if (dimensionProperty) {
          newDimensionProperties[dimension.name] = dimensionProperty
        }
      }
      this.query.dimensionProperties = newDimensionProperties
    }

    if (this.query.selection) {
      const newSelection = {}
      for (const dimension of this.query.dimensions) {
        const selection = this.query.selection[dimension.name]
        if (selection) {
          newSelection[dimension.name] = selection
        }
      }
      this.query.selection = newSelection
    }

    if (!this.dimensionsError) {
      this.removeIncompatibleMetrics()
    }
    return this
  }

  setDimensionProperties(dimensionProperties: any): QueryBuilder {
    this.query.dimensionProperties = dimensionProperties
    this.query.isDirty = true
    this.filterOrder()
    return this
  }

  setSelection(selection: any): QueryBuilder {
    this.query.selection = (selection || {})
    this.query.isDirty = true
    return this
  }

  setMetrics(metrics: Metric[]): QueryBuilder {
    this.query.metrics = [...metrics]
    this.query.isDirty = true
    this.metricsError = this.query.validateMetrics()
    this.filterOrder()
    this.isValid = this.query.isValid()
    return this
  }

  setDates(dates: DateRange): QueryBuilder {
    this.query.interval = `${format(dates.startDate, 'yyyyMMdd')}-${format(dates.endDate, 'yyyyMMdd')}`
    this.dates = dates
    return this
  }

  setOrder(orderedField): QueryBuilder {
    this.query.order = [orderedField].filter(Boolean)
    this.query.isDirty = true
    return this
  }

  setGroupBy(dimensions: Dimension[]): QueryBuilder {
    this.query.groupBy = [...dimensions]
    this.query.isDirty = true
    return this
  }

  setFilters(filters: IQueryFilterWrapper[]): QueryBuilder {
    this.query.filters = filters;
    this.query.isDirty = true;
    return this;
  }

  setMetricFilters(metricFilters: IMetricFilterWrapper[]): QueryBuilder {
    this.query.metricFilters = metricFilters;
    this.query.isDirty = true;
    return this;
  }

  editableDimensions(indexToExclude?: number) {
    let dimensions = this.query.dimensions
    if (typeof indexToExclude !== 'undefined'){
      dimensions = [...dimensions.slice(0, indexToExclude), ...dimensions.slice(indexToExclude + 1)]
    }
    const compatibleDimensions = this.schema ? this.schema.compatibleDimensions(dimensions) : []
    const requiredDimensions = this.schema ? this.schema.requiredDimensions() : []
    return sortBy(
      compatibleDimensions.filter(d => !requiredDimensions.includes(d)),
      'displayName'
    )
  }

  compatibleMetrics() {
    return this.schema ? this.schema.compatibleMetrics(this.query.dimensions, this.query.metrics) : []
  }

  private removeIncompatibleMetrics() {
    const compatibleMetricsNames = this.compatibleMetrics().map(m => m.name)
    const validMetrics = this.query.metrics.filter(m => compatibleMetricsNames.includes(m.name))
    if (validMetrics.length !== this.query.metrics.length) {
      this.setMetrics(validMetrics)
    }
  }

  private filterOrder() {
    const dimensions = this.query.dimensions.map(d => d.name);
    const metrics = this.query.metrics ? this.query.metrics.map(m => m.name) : [];
    const dimensionProperties = (this.query.dimensionProperties ? Object.values(this.query.dimensionProperties || {}).flat() : []) as string[];

    const validNames = [...dimensions, ...metrics, ...dimensionProperties];

    this.query.order = this.query.order.filter(o =>
      validNames.some(name => o.includes(name))
    );
  }
}
